import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, ANNOUNCEMENT_LABEL, resolveFileUrl } from "../lib/api";
import { Button } from "./ui";
import { Icon } from "./icons";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-PT", { day: "numeric", month: "short" }) : "");
const KIND_CLS = { comunicado: "doing", alerta: "todo", sondagem: "done" };

function Poll({ a, onVote, busy }) {
  const poll = a.poll;
  const mine = poll.my_vote || [];
  const toggle = (i) => {
    if (poll.closed) return;
    const next = poll.multiple ? (mine.includes(i) ? mine.filter((x) => x !== i) : [...mine, i]) : [i];
    if (next.length) onVote(next);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid={`announcement-poll-${a.id}`}>
      {poll.question ? <div style={{ fontFamily: "var(--f-medium)", fontSize: 15 }}>{poll.question}</div> : null}
      {poll.options.map((opt, i) => {
        const n = poll.counts?.[i] || 0;
        const pct = poll.voters ? Math.round((n / poll.voters) * 100) : 0;
        const sel = mine.includes(i);
        return (
          <button key={`${opt}-${i}`} onClick={() => toggle(i)} disabled={poll.closed || busy} aria-pressed={sel} data-testid={`announcement-${a.id}-option-${i}`}
            style={{ position: "relative", overflow: "hidden", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: "var(--r-md)", border: `1px solid ${sel ? "var(--brand-primary)" : "var(--border)"}`, background: "var(--surface-3)", color: "var(--on-surface)", cursor: poll.closed ? "default" : "pointer", textAlign: "left" }}>
            <span style={{ position: "absolute", inset: 0, width: `${pct}%`, background: sel ? "var(--brand-tertiary)" : "var(--surface-2)", transition: "width 0.3s" }} />
            <Icon name={sel ? "checkmark" : "add"} size={15} color={sel ? "var(--brand-primary)" : "var(--muted)"} style={{ position: "relative" }} />
            <span style={{ position: "relative", flex: 1 }}>{opt}</span>
            <span style={{ position: "relative", fontSize: 12, color: "var(--on-surface-3)" }}>{mine.length || poll.voters ? `${pct}% · ${n}` : ""}</span>
          </button>
        );
      })}
      <div className="t-hint">{poll.voters || 0} {poll.voters === 1 ? "resposta" : "respostas"}{poll.multiple ? " · pode escolher várias" : ""}{poll.closed ? " · sondagem fechada" : ""}</div>
    </div>
  );
}

/** One announcement as members see it (same fields and actions as the Mobile app). */
export function AnnouncementCard({ a, compact }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(!compact);
  const refresh = () => qc.invalidateQueries({ queryKey: ["announcements"] });
  const ack = useMutation({ mutationFn: () => api(`/announcements/${a.id}/ack`, { method: "POST" }), onSuccess: refresh });
  const vote = useMutation({ mutationFn: (options) => api(`/announcements/${a.id}/vote`, { method: "POST", body: JSON.stringify({ options }) }), onSuccess: refresh, onError: (e) => window.alert(e.message) });
  const atts = a.attachments || [];
  const images = atts.filter((x) => x.kind === "image");
  const files = atts.filter((x) => x.kind === "file");
  return (
    <div className="card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10, ...(a.type === "alerta" ? { borderColor: "var(--brand-primary)" } : {}) }} data-testid={`announcement-${a.id}`}>
      <button onClick={() => compact && setOpen((v) => !v)} style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "none", border: "none", color: "inherit", padding: 0, textAlign: "left", cursor: compact ? "pointer" : "default" }} data-testid={`announcement-${a.id}-head`}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="t-kicker"><span className={`pill ${KIND_CLS[a.type] || "todo"}`} style={{ marginRight: 6 }}>{(ANNOUNCEMENT_LABEL[a.type] || "Aviso").toUpperCase()}</span>{a.pinned ? "FIXADO · " : ""}{fmtDate(a.created_at)}</div>
          <div style={{ fontFamily: "var(--f-medium)", fontSize: 17, marginTop: 6 }}>{a.title}</div>
        </div>
        {a.require_ack && !a.my_ack ? <span style={{ width: 8, height: 8, borderRadius: 999, background: "var(--brand-primary)", marginTop: 6 }} data-testid={`announcement-${a.id}-unread`} /> : null}
        {compact ? <Icon name={open ? "chevron-up" : "chevron-down"} size={16} color="var(--muted)" /> : null}
      </button>
      {open ? (
        <>
          {a.body ? <div style={{ color: "var(--on-surface-3)", fontSize: 15, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{a.body}</div> : null}
          {images.length ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 8 }}>
              {images.map((img) => <a key={img.file_id} href={resolveFileUrl(img.url || "")} target="_blank" rel="noreferrer" data-testid={`announcement-image-${img.file_id}`}><img src={resolveFileUrl(img.url || "")} alt={img.filename || ""} style={{ width: "100%", aspectRatio: "4 / 3", objectFit: "cover", borderRadius: "var(--r-md)" }} /></a>)}
            </div>
          ) : null}
          {a.video_url ? <a href={a.video_url} target="_blank" rel="noreferrer" className="chip" style={{ alignSelf: "flex-start" }} data-testid={`announcement-video-${a.id}`}><Icon name="play" size={14} /> VER VÍDEO</a> : null}
          {files.map((f) => <a key={f.file_id} href={resolveFileUrl(f.url || "")} target="_blank" rel="noreferrer" className="chip" style={{ alignSelf: "flex-start" }} data-testid={`announcement-file-${f.file_id}`}><Icon name="document-text" size={14} /> {f.filename || "Ficheiro"}</a>)}
          {a.poll ? <Poll a={a} onVote={(o) => vote.mutate(o)} busy={vote.isPending} /> : null}
          {a.require_ack ? (a.my_ack ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--on-success)" }} data-testid={`announcement-${a.id}-acked`}><Icon name="checkmark" size={15} /> Leitura confirmada</div>
          ) : <Button className="auto sm" icon="checkmark" loading={ack.isPending} onClick={() => ack.mutate()} data-testid={`announcement-${a.id}-ack`}>CONFIRMAR LEITURA</Button>) : null}
        </>
      ) : null}
    </div>
  );
}

/** Home block: up to 3 active alerts (pinned first) with a link to all. */
export function HomeAnnouncements({ canManage }) {
  const q = useQuery({ queryKey: ["announcements"], queryFn: () => api("/announcements") });
  const list = [...(q.data || [])].sort((x, y) => (y.pinned ? 1 : 0) - (x.pinned ? 1 : 0)).slice(0, 3);
  if (!list.length) return null;
  return (
    <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 10 }} data-testid="home-announcements">
      <div className="row-between">
        <span className="t-section">Alertas</span>
        <Link to={canManage ? "/admin/announcements" : "/announcements"} className="t-hint" style={{ textDecoration: "none" }} data-testid="home-announcements-all">{canManage ? "Gerir" : "Ver todos"}</Link>
      </div>
      {list.map((a) => <AnnouncementCard key={a.id} a={a} compact />)}
    </div>
  );
}
